import React, { useEffect, useState } from "react";
import {
  createLegalDocumentUploadUrl,
  fetchLegalCoverage,
  fetchLegalVersions,
  publishLegalVersion,
  rollbackLegalVersion,
  searchLegalAcceptances,
} from "../../lib/legalGraphql";

export default function LegalAdmin() {
  const [tab, setTab] = useState("versions");
  const [versions, setVersions] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [acceptances, setAcceptances] = useState([]);
  const [tenantSearch, setTenantSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const refreshVersions = async () => {
    setVersions(await fetchLegalVersions());
  };

  const refreshCoverage = async () => {
    setCoverage(await fetchLegalCoverage());
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([refreshVersions(), refreshCoverage()])
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError("");
    try {
      const payload = await createLegalDocumentUploadUrl(file);
      const putRes = await fetch(payload.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "text/html" },
        body: file,
      });
      if (!putRes.ok) throw new Error("Falló la subida a S3");
      setInfo(`Archivo subido (${payload.upload_id}).`);
      await refreshVersions();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  const handlePublish = async (version) => {
    if (!window.confirm(`¿Publicar versión ${version}?`)) return;
    setLoading(true);
    try {
      await publishLegalVersion(version);
      setInfo(`Versión ${version} publicada.`);
      await Promise.all([refreshVersions(), refreshCoverage()]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async (version) => {
    if (!window.confirm(`¿Restaurar versión ${version}?`)) return;
    setLoading(true);
    try {
      await rollbackLegalVersion(version);
      setInfo(`Rollback a versión ${version}.`);
      await Promise.all([refreshVersions(), refreshCoverage()]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setError("");
    try {
      setAcceptances(await searchLegalAcceptances(tenantSearch.trim() || undefined));
      setTab("search");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && <p className="ops-error">{error}</p>}
      {info && <p className="ops-info">{info}</p>}

      <nav className="ops-nav">
        <button type="button" className={tab === "versions" ? "ops-tab active" : "ops-tab"} onClick={() => setTab("versions")}>
          Versiones
        </button>
        <button type="button" className={tab === "coverage" ? "ops-tab active" : "ops-tab"} onClick={() => setTab("coverage")}>
          Cobertura
        </button>
        <button type="button" className={tab === "search" ? "ops-tab active" : "ops-tab"} onClick={() => setTab("search")}>
          Búsqueda
        </button>
      </nav>

      {tab === "versions" && (
        <>
          <div className="ops-card">
            <h3>Subir documento</h3>
            <input type="file" accept=".html,text/html" onChange={handleUpload} disabled={loading} />
          </div>
          <div className="ops-card">
            <h3>Versiones</h3>
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Versión</th>
                  <th>Estado</th>
                  <th>Creada</th>
                  <th>Publicada</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.version}>
                    <td>{v.version}</td>
                    <td>{v.status}</td>
                    <td>{v.created_at || "—"}</td>
                    <td>{v.published_at || "—"}</td>
                    <td>
                      {v.status === "DRAFT" && (
                        <button type="button" className="ops-btn" onClick={() => handlePublish(v.version)}>
                          Publicar
                        </button>
                      )}
                      {(v.status === "ARCHIVED" || v.status === "PUBLISHED") && (
                        <button type="button" className="ops-btn secondary" onClick={() => handleRollback(v.version)}>
                          Restaurar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "coverage" && coverage && (
        <div className="ops-card">
          <h3>
            Cobertura v{coverage.version} — {coverage.covered_count} ok / {coverage.missing_count} pendientes
          </h3>
          <h4>Tenants sin aceptación vigente</h4>
          <ul>
            {coverage.missing.map((m) => (
              <li key={m.tenant_id}>
                {m.tenant_name || m.tenant_id}
              </li>
            ))}
          </ul>
          <h4>Aceptaron versión vigente</h4>
          <table className="ops-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Email</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {coverage.covered.map((c) => (
                <tr key={c.tenant_id}>
                  <td>{c.tenant_name || c.tenant_id}</td>
                  <td>{c.accepted_by_email || "—"}</td>
                  <td>{c.accepted_at || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "search" && (
        <div className="ops-card">
          <h3>Historial de aceptaciones</h3>
          <input
            placeholder="tenant_id o email…"
            value={tenantSearch}
            onChange={(e) => setTenantSearch(e.target.value)}
          />
          <button type="button" className="ops-btn secondary" style={{ marginLeft: 8 }} onClick={handleSearch}>
            Buscar
          </button>
          <table className="ops-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Versión</th>
                <th>Email</th>
                <th>Fecha</th>
                <th>Contexto</th>
              </tr>
            </thead>
            <tbody>
              {acceptances.map((a) => (
                <tr key={`${a.tenant_id}-${a.version}-${a.accepted_at}`}>
                  <td>{a.tenant_id}</td>
                  <td>{a.version}</td>
                  <td>{a.accepted_by_email || "—"}</td>
                  <td>{a.accepted_at}</td>
                  <td>{a.context}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && <p className="ops-loading">Cargando…</p>}
    </>
  );
}
